import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { isPersonNatural } from '@dealstack/shared/lib/common/business/person.ts';
import {
	normalizePhoneNumberForBE,
	normalizePhoneNumberForFormValue,
} from '@dealstack/shared/lib/format/string';
import { isPhoneValid } from '@dealstack/shared/lib/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	isPersonEmailInUse,
	updatePersonNatural,
	updatePersonNaturalEmail,
} from '@dealstack/frontend/app/services/Person.service.ts';
import {
	formatDateToBEIgnoreTimezone,
	parseBEDateIgnoreTimezone,
} from '@dealstack/shared/lib/format/dates/date';
import { Gender } from '@dealstack/shared/lib/generated';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { buildEmailChangeQueuedToast } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/NaturalPersonDetails/DetailsSection/useDetailsSection.utils.ts';

export enum NATURAL_PERSON_FORM_FIELDS {
	first_name = 'first_name',
	last_name = 'last_name',
	date_of_birth = 'date_of_birth',
	country_of_birth = 'country_of_birth',
	place_of_birth = 'place_of_birth',
	gender = 'gender',
	email = 'email',
	phone_number = 'phone_number',
	telephone_prefix = 'telephone_prefix',
	citizenship = 'citizenship',
	domicile = 'domicile',
}

const createNaturalPersonFormShape = (t: (key: string) => string) =>
	z.object({
		[NATURAL_PERSON_FORM_FIELDS.first_name]: z.string().min(1),
		[NATURAL_PERSON_FORM_FIELDS.last_name]: z.string().min(1),
		[NATURAL_PERSON_FORM_FIELDS.date_of_birth]: z.date().optional(),
		[NATURAL_PERSON_FORM_FIELDS.country_of_birth]: z.string().optional().nullable(),
		[NATURAL_PERSON_FORM_FIELDS.domicile]: z.string().optional().nullable(),
		[NATURAL_PERSON_FORM_FIELDS.citizenship]: z.array(z.string()).optional().nullable(),
		[NATURAL_PERSON_FORM_FIELDS.place_of_birth]: z.string().optional(),
		[NATURAL_PERSON_FORM_FIELDS.gender]: z.nativeEnum(Gender).optional(),
		[NATURAL_PERSON_FORM_FIELDS.email]: z
			.string()
			.refine((val) => !val || val === '' || z.string().email().safeParse(val).success, {
				message: t('basic_details.natural_person_form.invalid_email_format'),
			})
			.optional(),
		[NATURAL_PERSON_FORM_FIELDS.phone_number]: z
			.string()
			.optional()
			.refine(
				(data) => {
					if (!data) {
						return true;
					}
					return isPhoneValid(data);
				},
				{
					message: t('basic_details.natural_person_form.wrong_phone'),
				}
			),
	});

interface NaturalPersonFormValues {
	first_name: string;
	last_name: string;
	date_of_birth?: Date;
	country_of_birth?: string;
	place_of_birth?: string;
	gender?: Gender;
	email?: string;
	phone_number?: string;
	telephone_prefix?: string;
	citizenship?: string[];
	domicile?: string;
}

export const useDetailsSection = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	tDefault: TranslateFunction<I18Namespaces.DEFAULT>,
	hasSupportPermission: boolean,
	hasEditorPermission: boolean
) => {
	const {
		selectedEntityId,
		selectedEntityData,
		refetchSelectedEntity,
		refetchPersons,
		isFetchingSelectedEntityData,
		capForceDeleteFlag,
	} = useContext(CapManageControllerContext);

	const updateNaturalPersonDetailsRequest = useMutation({
		mutationKey: [API_PATHS.PERSON_PERSONID_NATURAL_PUT, { personId: selectedEntityId }],
		mutationFn: updatePersonNatural,
	});
	const updateNaturalPersonEmailRequest = useMutation({
		mutationKey: [API_PATHS.PERSON_PERSONID_NATURAL_EMAIL_PUT, { personId: selectedEntityId }],
		mutationFn: updatePersonNaturalEmail,

		onSuccess: (_data, variables) => {
			showToastV2(buildEmailChangeQueuedToast(tCapManage, variables.updateArgs.email));
		},

		onError: (error: { error?: { code?: number; message?: string } }) => {
			showToastV2({
				title: error.error?.code
					? tDefault(`error_codes.${error.error?.code}`)
					: error.error?.message || 'Failed to update email',
				state: 'error',
			});
		},
	});

	const isEmailInUseRequest = useQuery({
		queryKey: ['emailInUse', selectedEntityId],
		queryFn: () =>
			selectedEntityId && selectedEntityData?.email
				? isPersonEmailInUse(selectedEntityId)
				: false,
		enabled: !!selectedEntityId,
	});
	const canEditEmail = useMemo(
		() =>
			isEmailInUseRequest.data
				? hasSupportPermission
				: hasEditorPermission || hasSupportPermission,
		[isEmailInUseRequest.data, hasEditorPermission, hasSupportPermission]
	);
	// A person backed by a user account: email changes here also touch the account.
	const isLinkedToUser = !!selectedEntityData?.userId;

	const naturalPersonDetailsForm = useForm<NaturalPersonFormValues>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: zodResolver(createNaturalPersonFormShape(tCapManage)),
	});

	useEffect(() => {
		if (!selectedEntityData || !isPersonNatural(selectedEntityData.type)) {
			return;
		}

		naturalPersonDetailsForm.reset({
			[NATURAL_PERSON_FORM_FIELDS.first_name]: selectedEntityData.firstName,
			[NATURAL_PERSON_FORM_FIELDS.last_name]: selectedEntityData.lastName,
			[NATURAL_PERSON_FORM_FIELDS.date_of_birth]: selectedEntityData.dateOfBirth
				? parseBEDateIgnoreTimezone(selectedEntityData.dateOfBirth)
				: undefined,
			[NATURAL_PERSON_FORM_FIELDS.country_of_birth]:
				selectedEntityData.countryOfBirth ?? undefined,
			[NATURAL_PERSON_FORM_FIELDS.place_of_birth]:
				selectedEntityData.placeOfBirth ?? undefined,
			[NATURAL_PERSON_FORM_FIELDS.gender]: (selectedEntityData.gender as Gender) ?? undefined,
			[NATURAL_PERSON_FORM_FIELDS.email]: selectedEntityData.email ?? undefined,
			[NATURAL_PERSON_FORM_FIELDS.phone_number]:
				normalizePhoneNumberForFormValue(
					selectedEntityData.phoneNumber,
					selectedEntityData.telephonePrefix
				) ?? undefined,
			[NATURAL_PERSON_FORM_FIELDS.telephone_prefix]:
				selectedEntityData.telephonePrefix ?? undefined,
			[NATURAL_PERSON_FORM_FIELDS.citizenship]: selectedEntityData.citizenship ?? [],
			[NATURAL_PERSON_FORM_FIELDS.domicile]: selectedEntityData.domicile ?? undefined,
		});
	}, [selectedEntityId, selectedEntityData]);

	const onNaturalPersonFormSubmit = useCallback(async () => {
		const {
			first_name,
			last_name,
			date_of_birth,
			country_of_birth,
			place_of_birth,
			phone_number,
			domicile,
			citizenship,
			telephone_prefix,
			email,
		} = naturalPersonDetailsForm.getValues();

		const phoneNumber = normalizePhoneNumberForBE(phone_number, telephone_prefix);

		const mutations: Promise<unknown>[] = [];

		if (email && email !== selectedEntityData?.email && canEditEmail) {
			mutations.push(
				updateNaturalPersonEmailRequest.mutateAsync({
					personId: selectedEntityId,
					updateArgs: {
						email: email ?? '',
					},
				})
			);
		}

		mutations.push(
			updateNaturalPersonDetailsRequest.mutateAsync({
				personId: selectedEntityId,
				updateArgs: {
					firstName: first_name,
					lastName: last_name,
					countryOfBirth: country_of_birth,
					placeOfBirth: place_of_birth,
					citizenship,
					domicile,
					phoneNumber,
					telephonePrefix: telephone_prefix,
					dateOfBirth: date_of_birth
						? formatDateToBEIgnoreTimezone(date_of_birth)
						: undefined,
				},
			})
		);

		await Promise.all(mutations);

		void refetchSelectedEntity();
		void refetchPersons();
	}, [
		naturalPersonDetailsForm,
		selectedEntityId,
		selectedEntityData?.email,
		hasSupportPermission,
		updateNaturalPersonEmailRequest,
		updateNaturalPersonDetailsRequest,
		refetchSelectedEntity,
		refetchPersons,
	]);

	return {
		selectedEntityData,
		selectedEntityId,
		onNaturalPersonFormSubmit,
		naturalPersonDetailsForm,
		isLoading:
			isFetchingSelectedEntityData ||
			updateNaturalPersonDetailsRequest.isPending ||
			updateNaturalPersonEmailRequest.isPending,
		refetchPersons,
		capForceDeleteFlag,
		canEditEmail,
		isLinkedToUser,
	};
};
